import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Color, Loader } from '@qwealth/qcore';

import { useAppDispatch, useAppSelector } from 'data/store';
import { setIsLoading, setSelectedKyp } from 'data/actions/KYP';
import { selectKypIsLoading, selectKyps, selectKypSortBy, selectKypSortingDate, selectSelectedKyp } from 'data/selectors/kypSelectors';
import { Kyp, KypDateField, KypDateFieldNames, KypSortOption } from 'data/models/Kyp';
import { StyledTable, TableCell, TableHeader, TableHeaderWrapper, TableRowAnimated, TableWrapper } from './KypTable.styled';
import KypTableFooter from './KypTableFooter';
import { format } from 'date-fns';
import { getKypSortingFunction, getKypTableColumns } from 'utils/KypUtil';
import { upperFirst } from 'lodash';

const KypTable: FC = (): JSX.Element => {
  const [kypTableColumns, setKypTableColumns] = useState(getKypTableColumns());
  const isLoading = useAppSelector<boolean>(selectKypIsLoading);
  const kyps = useAppSelector<Kyp[]>(selectKyps);
  const sortBy = useAppSelector<KypSortOption | undefined>(selectKypSortBy);
  const sortingDate = useAppSelector(selectKypSortingDate);
  const selectedKyp = useAppSelector<Kyp | undefined>(selectSelectedKyp);
  const dispatch = useAppDispatch();

  useEffect(() => {
    setKypTableColumns(getKypTableColumns(sortingDate));
  }, [sortingDate]);

  const sortedKyps = useMemo<Kyp[]>(() => {
    dispatch(setIsLoading(true));

    const comparator = getKypSortingFunction(sortBy ? sortBy : 'Newest', sortingDate ? sortingDate : 'lastUpdated');
    const kypsSorted = [...kyps].sort(comparator);

    dispatch(setIsLoading(false));

    return kypsSorted;
  }, [kyps, sortBy]);

  const clickKyp = (kyp: Kyp) => {
    selectedKyp && kyp.uuid === selectedKyp.uuid ? dispatch(setSelectedKyp(undefined)) : dispatch(setSelectedKyp(kyp));
  };

  const formatCellValue = (kyp: Kyp, columnKey: string) => {
    const value = kyp[columnKey];
    if (value === undefined) return '-';

    if (KypDateFieldNames.includes(columnKey as KypDateField)) {
      return format(new Date(value), 'MMMM d, yyyy');
    }

    if (columnKey === 'reviewStatus') {
      const status = upperFirst(kyp.reviewStatus);
      const isApproved = status === 'Approved';
      const isExpired = status === 'Expired';

      return isApproved || isExpired ? (
        <Box color={isExpired ? 'red' : 'green'} fontWeight="bold">
          {status}
        </Box>
      ) : (
        status //No Styling for pending status
      );
    }

    return value;
  };

  return (
    <Box marginBottom="large">
      <TableWrapper>
        {isLoading ? (
          <Loader />
        ) : (
          <StyledTable>
            <TableHeaderWrapper>
              {kypTableColumns.map(col => (
                <TableHeader key={col.key}>{col.header}</TableHeader>
              ))}
            </TableHeaderWrapper>
            <tbody>
              {sortedKyps.map((kyp, index) => {
                return (
                  <TableRowAnimated
                    key={kyp.uuid}
                    index={index > 10 ? 10 : index}
                    isVisible={true}
                    isSelected={selectedKyp ? kyp.uuid === selectedKyp.uuid : false}
                    onClick={() => clickKyp(kyp)}
                  >
                    {kypTableColumns.map(col => (
                      <TableCell key={col.key}>{formatCellValue(kyp, col.key)}</TableCell>
                    ))}
                  </TableRowAnimated>
                );
              })}
            </tbody>
          </StyledTable>
        )}
      </TableWrapper>
      <KypTableFooter />
    </Box>
  );
};

export default KypTable;
